import { FC } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { Flex, Text, Box } from "@chakra-ui/react";
import { routes } from "~/constants";
import { useCurrentDiagnosticUser } from "~/hooks/useCurrentDiagnosticUser";
import { ProductLogo, TwoPanelLayout } from "../components";
import { PRODUCT_MAPPING } from "../constants";

const ConfirmationPage: FC = () => {
  const { t } = useTranslation();
  const currentDiagnosticUser = useCurrentDiagnosticUser();
  const [searchParams] = useSearchParams();
  const productSlug = searchParams.get("slug") as string;
  const showBookAppointmentButton = false; // Toggle this to show/hide the button

  const leftContent = (
    <Flex direction="column" gap={4} alignItems="flex-start">
      <Text
        fontSize="44px"
        fontWeight="700"
        lineHeight="1.1"
        fontFamily="Moderat Extended Bold, sans-serif"
        color="th.black"
        maxW="464px"
      >
        {t("diagnosticDashboard.confirmation.heading")}
      </Text>
      <Text
        fontSize="18px"
        fontWeight="400"
        lineHeight="1.4"
        color="th.black"
        maxW="464px"
      >
        {t(`diagnosticDashboard.confirmation.subheading.${productSlug}` as any, {
          defaultValue: t("diagnosticDashboard.confirmation.defaultSubheading", { productName: PRODUCT_MAPPING[productSlug]?.name }),
          productName: PRODUCT_MAPPING[productSlug]?.name
        })}
      </Text>

      <Box
        px={5}
        py={4}
        bg="white"
        borderRadius="lg"
        width="100%"
        mt={4}
        boxShadow="0px 2px 4px 0px rgba(0, 0, 0, 0.1)"
      >
        <Flex direction="column" gap={5}>
          <Flex alignItems="center" gap="14px">
            <ProductLogo slug={productSlug} boxSize="45px" />
            <Box>
              <Text
                fontWeight="bold"
                fontSize="18px"
                color="th.black"
                lineHeight="1.4"
              >
                {PRODUCT_MAPPING[productSlug].name}
              </Text>
              <Text fontSize="18px" color="th.black" lineHeight="1.4">
                {currentDiagnosticUser?.firstName}{" "}
                {currentDiagnosticUser?.lastName}
              </Text>
            </Box>
          </Flex>
          {/* Optional Book Appointment Button */}
          {showBookAppointmentButton && (
            <Link
              to={routes.diagnosticsDashboard}
              style={{ textDecoration: "none" }}
            >
              <Flex
                bg="th.signatureGreen"
                borderRadius="xl"
                justify="center"
                align="center"
                py="14px"
                px={6}
                cursor="pointer"
                _hover={{ bg: "newDarkGreen.700" }}
                transition="background 0.2s"
              >
                <Text
                  fontSize="14px"
                  fontWeight="bold"
                  color="white"
                  lineHeight="1.4"
                >
                  {t("diagnosticDashboard.confirmation.bookAppointment")}
                </Text>
              </Flex>
            </Link>
          )}
        </Flex>
      </Box>
    </Flex>
  );

  const rightContent = (
    <Flex direction="column" gap="35px" pb="120px">
      {/* SVG with clipPath definition */}
      <svg width="0" height="0" style={{ position: "absolute" }}>
        <defs>
          <clipPath id="oval" clipPathUnits="objectBoundingBox">
            <path
              d="M0.847609 0.777907C0.664096 1.00743 0.359711 1.06907 0.167745 0.91559C-0.024221 0.762107 -0.0310734 0.451618 0.15244 0.222093C0.335952 -0.00743086 0.640338 -0.0690741 0.832304 0.0844095C1.02427 0.237893 1.03112 0.548382 0.847609 0.777907Z"
              fill="white"
            />
          </clipPath>
        </defs>
      </svg>

      {/* What's next section */}
      <Text
        fontSize="28px"
        fontWeight="700"
        fontFamily="Moderat Extended Bold, sans-serif"
        lineHeight="1.2"
        color="th.black"
      >
        {t("diagnosticDashboard.confirmation.whatsNext")}
      </Text>

      <Flex direction="column" gap="30px" width="100%">
        <Flex align="flex-start" gap={5}>
          <Flex
            align="center"
            justify="center"
            bg="th.peach1"
            color="th.black"
            fontSize="18px"
            fontWeight="700"
            fontFamily="Moderat Bold, sans-serif"
            h="48px"
            w="48px"
            flexShrink={0}
            clipPath="url(#oval)"
          >
            1
          </Flex>
          <Flex direction="column" gap="10px" maxW="395px">
            <Text
              fontSize="18px"
              fontWeight="700"
              color="th.black"
              lineHeight="1.4"
            >
              {t(`diagnosticDashboard.confirmation.step1.${productSlug}.title` as any, {
                defaultValue: t("diagnosticDashboard.confirmation.step1Title")
              })}
            </Text>
            <Text
              color="th.black"
              fontSize="16px"
              fontWeight="400"
              lineHeight="1.4"
            >
              {t(`diagnosticDashboard.confirmation.step1.${productSlug}.description` as any, {
                defaultValue: t("diagnosticDashboard.confirmation.step1Description")
              })}
            </Text>
          </Flex>
        </Flex>

        <Flex align="flex-start" gap={5}>
          <Flex
            align="center"
            justify="center"
            bg="th.peach1"
            color="th.black"
            fontSize="18px"
            fontWeight="700"
            fontFamily="Moderat Bold, sans-serif"
            h="48px"
            w="48px"
            flexShrink={0}
            clipPath="url(#oval)"
          >
            2
          </Flex>
          <Flex direction="column" gap="10px" maxW="395px">
            <Text
              fontSize="18px"
              fontWeight="700"
              color="th.black"
              lineHeight="1.4"
            >
              {t(`diagnosticDashboard.confirmation.step2.${productSlug}.title` as any, {
                defaultValue: t("diagnosticDashboard.confirmation.step2Title")
              })}
            </Text>
            <Text
              color="th.black"
              fontSize="16px"
              fontWeight="400"
              lineHeight="1.4"
            >
              {t(`diagnosticDashboard.confirmation.step2.${productSlug}.description` as any, {
                defaultValue: t("diagnosticDashboard.confirmation.step2Description")
              })}
            </Text>
          </Flex>
        </Flex>

        <Flex align="flex-start" gap={5}>
          <Flex
            align="center"
            justify="center"
            bg="th.peach1"
            color="th.black"
            fontSize="18px"
            fontWeight="700"
            fontFamily="Moderat Bold, sans-serif"
            h="48px"
            w="48px"
            flexShrink={0}
            clipPath="url(#oval)"
          >
            3
          </Flex>
          <Flex direction="column" gap="10px" maxW="395px">
            <Text
              fontSize="18px"
              fontWeight="700"
              color="th.black"
              lineHeight="1.4"
            >
              {t(`diagnosticDashboard.confirmation.step3.${productSlug}.title` as any, {
                defaultValue: t("diagnosticDashboard.confirmation.step3Title")
              })}
            </Text>
            <Text
              color="th.black"
              fontSize="16px"
              fontWeight="400"
              lineHeight="1.4"
            >
              {t(`diagnosticDashboard.confirmation.step3.${productSlug}.description` as any, {
                defaultValue: t("diagnosticDashboard.confirmation.step3Description")
              })}
            </Text>
          </Flex>
        </Flex>

        {productSlug === "mens_basics" && (
          <Flex align="flex-start" gap={5}>
            <Flex
              align="center"
              justify="center"
              bg="th.peach1"
              color="th.black"
              fontSize="18px"
              fontWeight="700"
              fontFamily="Moderat Bold, sans-serif"
              h="48px"
              w="48px"
              flexShrink={0}
              clipPath="url(#oval)"
            >
              4
            </Flex>
            <Flex direction="column" gap="10px" maxW="395px">
              <Text
                fontSize="18px"
                fontWeight="700"
                color="th.black"
                lineHeight="1.4"
              >
                {t(`diagnosticDashboard.confirmation.step4.${productSlug}.title`)}
              </Text>
              <Text
                color="th.black"
                fontSize="16px"
                fontWeight="400"
                lineHeight="1.4"
              >
                {t(`diagnosticDashboard.confirmation.step4.${productSlug}.description`)}
              </Text>
            </Flex>
          </Flex>
        )}
      </Flex>

      <Link
        to={routes.diagnosticsDashboard}
        style={{ textDecoration: "none", width: "100%" }}
      >
        <Flex
          border="1px solid"
          borderColor="th.black"
          borderRadius="xl"
          h="52px"
          justify="center"
          align="center"
          px={6}
          py={4}
          cursor="pointer"
          _hover={{ bg: "gray.50" }}
          transition="background 0.2s"
        >
          <Text
            fontSize="14px"
            fontWeight="bold"
            color="th.black"
            lineHeight="1.4"
          >
            {t("diagnosticDashboard.confirmation.goToDashboard")}
          </Text>
        </Flex>
      </Link>
    </Flex>
  );

  return (
    <TwoPanelLayout leftContent={leftContent} rightContent={rightContent} />
  );
};

export { ConfirmationPage };
